import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  Heart, 
  Users, 
  AlertCircle, 
  Target, 
  FileText, 
  Phone, 
  Calendar,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      id: 'curriculum',
      icon: BookOpen,
      title: 'Guidance Curriculum',
      description: 'Structured developmental experiences presented systematically through group activities.',
      details: [
        'Structured developmental experiences from Kindergarten through College level',
        'Knowledge of personal, social and career development',
        'Promotion of positive mental health',
        'Assistance in acquiring and using life skills',
        'Systematic group activities for all students'
      ],
      whoItsFor: 'All students at all levels',
      howToAccess: 'Integrated into regular school programs with faculty cooperation'
    },
    {
      id: 'planning',
      icon: Target,
      title: 'Individual Planning',
      description: 'Activities that help students explore, plan, monitor and manage their own learning.',
      details: [
        'Personal development planning',
        'Academic goal evaluation',
        'Career development planning',
        'Learning management strategies',
        'Counselor-planned and directed activities'
      ],
      whoItsFor: 'All students seeking to manage their personal, academic and career development',
      howToAccess: 'Schedule individual planning sessions with counselors'
    },
    {
      id: 'responsive',
      icon: Heart,
      title: 'Responsive Services',
      description: 'Activities to meet the immediate needs and concerns of students.',
      details: [
        'Counseling for immediate needs',
        'Consultation services',
        'Referral to specialized services',
        'Information and guidance',
        'Support for urgent student concerns'
      ],
      whoItsFor: 'All students with immediate needs or concerns',
      howToAccess: 'Available to all students through counselor coordination with faculty and staff'
    },
    {
      id: 'system',
      icon: Users,
      title: 'System Support',
      description: 'Management activities that establish, maintain, and enhance the total guidance program.',
      details: [
        'Program establishment and maintenance',
        'Enhancement of guidance services',
        'Administrative support activities',
        'Program evaluation and improvement',
        'Coordination of guidance resources'
      ],
      whoItsFor: 'Supporting the entire guidance program infrastructure',
      howToAccess: 'Managed by guidance personnel and administration'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-800 to-blue-900 text-white py-16">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold mb-6">Our Services</h1>
            <p className="text-xl text-blue-100">
              Comprehensive guidance services designed to support every aspect of your educational journey and personal development.
            </p>
          </div>
        </div>
      </section>

      {/* Goal and Objectives Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="mb-12">
              <h2 className="section-title text-center">Our Goal</h2>
              <p className="text-gray-700 text-lg leading-relaxed text-center">
                To offer a quality program that provides students with various activities to help them boost their self-esteem and be more self-aware to discover full potential, improve relationships to effectively relate with other people and unveil a deeper meaning and purpose in life.
              </p>
            </div>
            
            <div>
              <h2 className="section-title text-center">Objectives</h2>
              <p className="text-gray-700 leading-relaxed">
                The Guidance Services aim in general to assist the individual student to discover their giftedness, to understand and accept themselves in relation to their decision making competencies. Furthermore, it aims to instill in the students the core values which includes love for the poor, simplicity of lifestyle, commitment to academic and Vincentian Excellence and professional competence. It also aims to help the students realize their calling as an agent of transformation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container-custom">
          <div className="grid gap-12">
            {services.map((service, index) => (
              <div key={service.id} className={`grid lg:grid-cols-2 gap-8 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
              }`}>
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <service.icon className="w-6 h-6 text-blue-800" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800">{service.title}</h2>
                  </div>
                  
                  <p className="text-gray-600 mb-6 text-lg">{service.description}</p>
                  
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-2">What We Offer:</h3>
                      <ul className="space-y-2">
                        {service.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4 pt-4">
                      <div>
                        <h4 className="font-medium text-gray-800 mb-2">Who It's For:</h4>
                        <p className="text-gray-600 text-sm">{service.whoItsFor}</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-800 mb-2">How to Access:</h4>
                        <p className="text-gray-600 text-sm">{service.howToAccess}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Image/Visual */}
                <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                  <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg p-8 h-64 flex items-center justify-center">
                    <service.icon className="w-24 h-24 text-blue-800 opacity-50" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Get Started */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title">How to Get Started</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Getting the support you need is easy. Follow these simple steps to access our services.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-blue-800" />
              </div>
              <h3 className="text-xl font-semibold mb-4">1. Schedule</h3>
              <p className="text-gray-600">
                Contact us to schedule an appointment or visit during walk-in hours. 
                We'll find a time that works for you.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-blue-800" />
              </div>
              <h3 className="text-xl font-semibold mb-4">2. Meet</h3>
              <p className="text-gray-600">
                Meet with one of our professional counselors to discuss your needs 
                and develop a personalized support plan.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-blue-800" />
              </div>
              <h3 className="text-xl font-semibold mb-4">3. Achieve</h3>
              <p className="text-gray-600">
                Work with us to achieve your goals through ongoing support, 
                resources, and follow-up sessions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-blue-800 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Our team is here to support you. Reach out today to schedule your first appointment 
            or learn more about our services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-secondary">
              <Phone className="w-5 h-5 mr-2" />
              Contact Us
            </Link>
            <Link to="/forms" className="btn-outline border-white text-white hover:bg-white hover:text-blue-800">
              <FileText className="w-5 h-5 mr-2" />
              Complete Assessment Form
            </Link>
          </div>
        </div>
      </section>

      {/* Emergency Resources */}
      <section className="py-12 bg-yellow-50 border-l-4 border-yellow-400">
        <div className="container-custom">
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-yellow-600 mr-4 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">Crisis Resources</h3>
              <p className="text-yellow-700 mb-4">
                If you're experiencing a mental health emergency, please reach out immediately:
              </p>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div>
                  <strong>Crisis Hotline:</strong><br />
                  <span className="text-yellow-800">988 (Suicide & Crisis Lifeline)</span>
                </div>
                <div>
                  <strong>Campus Emergency:</strong><br />
                  <span className="text-yellow-800">Call Campus Security</span>
                </div>
                <div>
                  <strong>After Hours:</strong><br />
                  <span className="text-yellow-800">Visit nearest emergency room</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;